import React from 'react';
import { Button } from './ui/button';
import { CheckCircle2, XCircle, CalendarDays, ClipboardList, FileSignature } from 'lucide-react';
import { unitAbbr } from '../ops/priceBookConstants';
import { DocHeader, DocFooter } from './DocBrand';

const money = (c) => (c == null ? '' : (c / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const fmtQty = (q) => {
  const n = Number(q);
  return Number.isNaN(n) ? q : n.toLocaleString('en-US', { maximumFractionDigits: 6 });
};
const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); } catch { return d; }
};

const GoldLabel = ({ children }) => (
  <div className="font-brand text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-1">{children}</div>
);

const SectionHeading = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <ClipboardList size={22} className="text-[#D4AF37] shrink-0" />
    <h2 className="font-brand text-lg sm:text-xl font-black uppercase tracking-wide text-gray-900">{children}</h2>
    <div className="flex-1 h-[2px] bg-gradient-to-r from-[#D4AF37] to-[#D4AF37]/20 rounded" />
  </div>
);

const ChecklistBlock = ({ title, text, emptyText, testId }) => (
  <div>
    <h3 className="font-brand text-sm font-extrabold uppercase tracking-wider text-gray-900 border-b-2 border-[#D4AF37] pb-2 mb-3">{title}</h3>
    <div className="flex items-start gap-2 text-sm text-gray-700">
      <CheckCircle2 size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
      <p className="whitespace-pre-wrap" data-testid={testId}>{text || emptyText}</p>
    </div>
  </div>
);

// Exact customer-facing quote document. Used by the public quote page AND the ops preview dialog.
export const QuoteDocumentView = ({ q, previewMode = false, onApprove, onDecline }) => {
  const address = [q.address_line1, q.address_line2, [q.city, q.state].filter(Boolean).join(', '), q.zip_code].filter(Boolean).join(', ');

  const rows = [];
  let lastSection = null, lastGroup = null, lineNo = 0;
  q.lines.forEach((l, i) => {
    if ((l.section_name || null) !== lastSection) {
      lastSection = l.section_name || null;
      lastGroup = null;
      if (lastSection) rows.push({ type: 'section', key: `s-${i}`, label: lastSection });
    }
    if (l.assembly_group_key && l.assembly_group_key !== lastGroup) {
      lastGroup = l.assembly_group_key;
      rows.push({ type: 'group', key: `g-${i}`, label: l.assembly_group_name });
    } else if (!l.assembly_group_key) {
      lastGroup = null;
    }
    lineNo += 1;
    rows.push({ type: 'line', key: `l-${i}`, line: l, no: String(lineNo).padStart(2, '0') });
  });

  return (
    <>
      {/* Status banners */}
      {q.status === 'accepted' && (
        <div className="mb-4 bg-green-600 text-white rounded-lg px-4 py-3 flex items-center gap-2 font-semibold" data-testid="public-approved-banner">
          <CheckCircle2 size={18} /> Approved{q.accepted_by_customer_name && <span className="font-normal">— by {q.accepted_by_customer_name} on {new Date(q.accepted_at).toLocaleDateString()}</span>}
        </div>
      )}
      {q.status === 'declined' && (
        <div className="mb-4 bg-red-600 text-white rounded-lg px-4 py-3 flex items-center gap-2 font-semibold" data-testid="public-declined-banner">
          <XCircle size={18} /> Declined
        </div>
      )}
      {q.is_expired && (
        <div className="mb-4 bg-amber-500 text-black rounded-lg px-4 py-3 text-sm font-medium" data-testid="public-expired-banner">
          {q.expired_message}
        </div>
      )}

      {/* Document */}
      <div className="bg-[#FAFAF8] rounded-xl shadow-2xl overflow-hidden border border-[#D4AF37]/30">
        <DocHeader />

        <div className="px-5 sm:px-8 py-7">
          {/* Info grid: quote card + prepared for / job location */}
          <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,340px)_1fr] gap-6 items-start">
            <div className="bg-black rounded-lg p-5 border-l-4 border-[#D4AF37]" data-testid="quote-info-card">
              <GoldLabel>Quote</GoldLabel>
              <div className="text-white font-brand font-extrabold text-xl tracking-tight" data-testid="public-quote-number">{q.quote_number}</div>
              <div className="h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent my-4" />
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CalendarDays size={18} className="text-[#D4AF37] shrink-0" />
                  <div>
                    <div className="font-brand text-[9px] font-bold uppercase tracking-[0.18em] text-gray-400">Quote Date</div>
                    <div className="text-white text-sm font-semibold">{fmtDate(q.quote_date)}</div>
                  </div>
                </div>
                {q.valid_until && (
                  <div className="flex items-center gap-3">
                    <CalendarDays size={18} className="text-[#D4AF37] shrink-0" />
                    <div>
                      <div className="font-brand text-[9px] font-bold uppercase tracking-[0.18em] text-gray-400">Valid Until</div>
                      <div className="text-white text-sm font-semibold">{fmtDate(q.valid_until)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="md:pt-1">
              <h1 className="font-brand text-2xl sm:text-3xl font-black text-gray-900 tracking-tight" data-testid="public-quote-title">{q.title}</h1>
              <div className="mt-4">
                <GoldLabel>Prepared For</GoldLabel>
                <div className="text-gray-900 font-semibold" data-testid="public-customer-name">{q.customer_name}</div>
              </div>
              {address && (
                <div className="mt-4">
                  <GoldLabel>Job Location</GoldLabel>
                  <div className="text-gray-800" data-testid="public-job-location">{address}</div>
                </div>
              )}
            </div>
          </div>

          {/* Scope of work */}
          <div className="mt-10">
            <SectionHeading>Scope of Work</SectionHeading>
            {q.scope_summary && <p className="text-sm text-gray-700 whitespace-pre-wrap mb-5" data-testid="public-scope-summary">{q.scope_summary}</p>}

            <div className="rounded-lg overflow-hidden border border-gray-200" data-testid="public-lines">
              {/* Table header */}
              <div className="grid grid-cols-[2.5rem_1fr_6.5rem] sm:grid-cols-[3.5rem_1fr_12rem_8rem] bg-black px-3 sm:px-4 py-3">
                <span className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37]">#</span>
                <span className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37]">Description</span>
                <span className="hidden sm:block font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37]">Details</span>
                <span className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37] text-right">Total</span>
              </div>
              <div className="bg-white">
                {rows.map((r) => {
                  if (r.type === 'section') return <div key={r.key} className="font-brand text-xs font-extrabold text-gray-900 uppercase tracking-wider bg-[#D4AF37]/10 border-y border-[#D4AF37]/25 px-4 py-2">{r.label}</div>;
                  if (r.type === 'group') return <div key={r.key} className="font-brand text-[11px] font-bold text-[#8a6d1f] uppercase tracking-wider px-4 pt-3">{r.label}</div>;
                  const l = r.line;
                  return (
                    <div key={r.key} className="grid grid-cols-[2.5rem_1fr_6.5rem] sm:grid-cols-[3.5rem_1fr_12rem_8rem] items-start px-3 sm:px-4 py-3.5 border-b border-[#D4AF37]/15 last:border-b-0">
                      <span className="text-xs font-bold text-gray-400 pt-0.5">{r.no}</span>
                      <div className="min-w-0 pr-3">
                        <div className="text-sm font-bold text-gray-900">{l.name}</div>
                        {l.description && <p className="text-xs text-gray-500 mt-0.5">{l.description}</p>}
                        <div className="sm:hidden text-xs text-gray-500 mt-1">{fmtQty(l.quantity_decimal)} {unitAbbr(l)}</div>
                      </div>
                      <span className="hidden sm:block text-sm text-gray-600 pt-0.5">{fmtQty(l.quantity_decimal)} {unitAbbr(l)}</span>
                      <span className="text-sm font-bold text-gray-900 text-right">{money(l.customer_line_total_cents)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Total + exclusions + terms */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(240px,320px)_1fr_1fr] gap-8 mt-10 items-start">
            <div className="bg-black rounded-lg px-6 py-5 border-l-4 border-[#D4AF37]">
              {q.discount_cents ? (
                <div className="space-y-1.5 mb-3 text-sm">
                  <div className="flex justify-between text-gray-300"><span>Subtotal</span><span data-testid="public-quote-subtotal">{money(q.subtotal_cents)}</span></div>
                  <div className="flex justify-between text-[#D4AF37] font-semibold"><span>Discount ({q.discount_percent}%)</span><span data-testid="public-quote-discount">−{money(q.discount_cents)}</span></div>
                  <div className="h-px bg-[#D4AF37]/30" />
                </div>
              ) : null}
              <GoldLabel>Quote Total</GoldLabel>
              <div className="font-brand font-black text-3xl sm:text-4xl text-[#D4AF37] tracking-tight" data-testid="public-quote-total">{money(q.quote_total_cents)}</div>
            </div>
            <ChecklistBlock title="Exclusions" text={q.exclusions} emptyText="No exclusions" testId="public-exclusions" />
            <ChecklistBlock title="Terms" text={q.terms} emptyText="No terms" testId="public-terms" />
          </div>

          {/* Respond */}
          {q.can_respond && (
            <div className="mt-10 border-2 border-[#D4AF37] rounded-xl p-5 sm:p-6 flex flex-col lg:flex-row flex-wrap items-center gap-5 lg:gap-6 bg-white" data-testid="public-response-panel">
              <div className="hidden xl:flex w-14 h-14 rounded-full border-2 border-[#D4AF37] items-center justify-center shrink-0">
                <FileSignature size={24} className="text-[#D4AF37]" />
              </div>
              <div className="text-center lg:text-left flex-1 min-w-[180px]">
                <h2 className="font-brand text-gray-900 font-black uppercase tracking-wider text-base whitespace-nowrap">Ready to Respond?</h2>
                <p className="text-xs text-gray-500 mt-1">Review the scope and total above, then choose your response.</p>
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
                <Button onClick={onApprove} disabled={previewMode} className="bg-green-600 hover:bg-green-500 text-white font-brand font-extrabold uppercase tracking-wide px-6 py-6 text-sm rounded-lg" data-testid="public-approve-btn">
                  <CheckCircle2 size={18} /> Approve Quote
                </Button>
                <Button onClick={onDecline} disabled={previewMode} className="bg-red-600 hover:bg-red-500 text-white font-brand font-extrabold uppercase tracking-wide px-6 py-6 text-sm rounded-lg" data-testid="public-decline-btn">
                  <XCircle size={18} /> Decline Quote
                </Button>
              </div>
              {previewMode && <p className="text-[11px] text-gray-400 w-full text-center lg:text-left lg:pl-20">Response buttons are disabled in preview.</p>}
            </div>
          )}
        </div>

        <DocFooter />
      </div>
    </>
  );
};
